import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";

import "./App.css";
import "./Layout.css";

import LeftSidebar from "./components/layout/LeftSidebar";
import TopBar from "./components/layout/TopBar";
import ScrollToTop from "./components/layout/ScrollToTop";
import Dashboard from "./components/Dashboard";
import TaskList from "./components/TaskList";
import CalendarView from "./components/CalendarView";
import Teams from "./components/Teams";
import Users from "./components/Users";
import TaskFormRoute from "./components/TaskFormRoute";
import TaskForm from "./components/TaskForm";
import LoginPage from "./pages/LoginPage";
import AuthSuitePage from "./pages/AuthSuitePage";
import ChangeLog from "./pages/ChangeLog";

import { useUser } from "./context/UserContext";
import { useNotification } from "./context/NotificationContext";
import {
  supabase,
  createTaskSupabase,
  updateTaskSupabase,
  TASK_SELECT
} from "./supabaseClient";
import ErrorBoundary from "./components/ErrorBoundary";

function AuthRedirect() {
  useEffect(() => {
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== "/" && currentPath !== "/login") {
      localStorage.setItem("redirect_after_login", currentPath);
    }
  }, []);
  return <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    </Router>
  );
}

function AppContent() {
  const { showAlert } = useNotification();
  const { currentUser, loading } = useUser();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("agsTheme") === "dark";
  });

  useEffect(() => {
    if (darkMode) {
      localStorage.setItem("agsTheme", "dark");
      document.body.classList.add("dark");
      document.body.classList.add("dark-theme");
      document.body.style.backgroundColor = "var(--bg-main, #0f172a)";
      document.body.style.color = "var(--text-main, #f8fafc)";
    } else {
      localStorage.setItem("agsTheme", "light");
      document.body.classList.remove("dark");
      document.body.classList.remove("dark-theme");
      document.body.style.backgroundColor = "var(--bg-main, #f7f9fb)";
      document.body.style.color = "var(--text-main, #0f172a)";
    }
  }, [darkMode]);

  const [tasks, setTasks] = useState([]);

  const handleUpdateTaskInState = useCallback((updatedTask) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === updatedTask.id ? updatedTask : t))
    );
  }, []);
  const [teams, setTeams] = useState([]);
  const [subTeams, setSubTeams] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [userMemberships, setUserMemberships] = useState([]);

  const getUserTeamIds = useCallback(async () => {
    if (!currentUser?.id) return [];

    const { data, error } = await supabase
      .from("user_team_memberships")
      .select("team_id, department_id, role, is_department_admin")
      .eq("user_id", currentUser.id);

    if (error) {
      console.error("Failed to load user memberships:", error);
      return [];
    }

    setUserMemberships(data || []);

    return [...new Set((data || []).map((m) => m.team_id).filter(Boolean))];
  }, [currentUser?.id]);

  const refreshUsers = useCallback(async () => {
    try {
      const { data: remoteUsers, error: usersError } = await supabase
        .from("profiles")
        .select("*")
        .order("name", { ascending: true });
      if (usersError) throw usersError;
      if (Array.isArray(remoteUsers)) setUsersList(remoteUsers);
    } catch (err) {
      console.error("App: refreshUsers failed:", err);
    }
  }, []);

  const refreshTeams = useCallback(async () => {
    try {
      const { data: remoteTeams } = await supabase
        .from("teams")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      if (remoteTeams) setTeams(remoteTeams);

      const { data: remoteSubTeams } = await supabase
        .from("sub_teams")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      if (remoteSubTeams) setSubTeams(remoteSubTeams);
    } catch (err) {
      console.warn("Failed to refresh teams:", err);
    }
  }, []);



  useEffect(() => {
    let mounted = true;

    async function loadAll() {
      if (!supabase) return;

      try {
        await getUserTeamIds();

        const { data: remoteTasks, error: tasksError } = await supabase
          .from("tasks")
          .select(TASK_SELECT)
          .is("deleted_at", null)
          .order("created_at", { ascending: true });

        if (tasksError) throw tasksError;
        if (mounted && Array.isArray(remoteTasks)) setTasks(remoteTasks);
      } catch (err) {
        console.warn("App: Failed to load tasks via helper:", err);
      }

      try {
        const { data: remoteTeams, error: teamsError } = await supabase
          .from("teams")
          .select("*")
          .order("created_at", { ascending: true });
        if (teamsError) throw teamsError;
        if (mounted && Array.isArray(remoteTeams)) setTeams(remoteTeams);
      } catch (err) {
        console.warn("Failed to load teams:", err);
      }

      try {
        const { data: remoteSubTeams, error: subTeamsError } = await supabase
          .from("sub_teams")
          .select("*")
          .is("deleted_at", null)
          .order("created_at", { ascending: true });
        if (subTeamsError) throw subTeamsError;
        if (mounted && Array.isArray(remoteSubTeams)) setSubTeams(remoteSubTeams);

        const { data: remoteDepts } = await supabase
          .from("departments")
          .select("*")
          .order("name");
        if (remoteDepts) setDepartments(remoteDepts);
      } catch (err) {
        console.warn("Failed to load sub-teams:", err);
      }

      await refreshUsers();
    }

    loadAll();

    const taskChannel = supabase
      ? supabase
        .channel("public:tasks")
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "tasks" },
          (payload) => {
            if (!mounted) return;
            const ev = payload.eventType;
            const row = ev === "DELETE" ? payload.old : payload.new;
            if (!row) return;

            if (ev === "INSERT" || ev === "UPDATE") {
              if (row.deleted_at) {
                setTasks((prev) => prev.filter((t) => t.id !== row.id));
                return;
              }

              // Fetch the fully joined task to keep relationships (e.g. task_assignees) intact
              supabase
                .from("tasks")
                .select(TASK_SELECT)
                .eq("id", row.id)
                .single()
                .then(({ data, error }) => {
                  if (!error && data && mounted) {
                    setTasks((prev) => {
                      const exists = prev.some((t) => t.id === data.id);
                      if (exists) {
                        return prev.map((t) => (t.id === data.id ? data : t));
                      }
                      return [...prev, data];
                    });
                  }
                })
                .catch((err) => {
                  console.error("Failed to fetch task on Postgres change:", err);
                });
            } else if (ev === "DELETE") {
              setTasks((prev) => prev.filter((t) => t.id !== row.id));
            }
          }
        )
        .subscribe()
      : null;

    const taskAssigneesChannel = supabase
      ? supabase
          .channel("public:task_assignees")
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "task_assignees" },
            async () => {
              if (!mounted) return;

              await getUserTeamIds();

              const { data, error } = await supabase
                .from("tasks")
                .select(TASK_SELECT)
                .is("deleted_at", null)
                .order("created_at", { ascending: true });

              if (error) {
                console.error("Task assignees refresh failed:", error);
                return;
              }

              if (mounted) setTasks(data || []);
            }
          )
          .subscribe()
      : null;

    const teamChannel = supabase
      ? supabase
        .channel("public:teams")
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "teams" },
          (payload) => {
            if (!mounted) return;
            const ev = payload.eventType;
            const row = ev === "DELETE" ? payload.old : payload.new;
            if (!row) return;

            if (ev === "INSERT") {
              setTeams((prev) => {
                if (row.deleted_at) return prev.filter((t) => t.id !== row.id);
                const exists = prev.some((t) => t.id === row.id);
                if (exists) return prev.map((t) => (t.id === row.id ? row : t));
                return [...prev, row];
              });
            } else if (ev === "UPDATE") {
              setTeams((prev) => {
                if (row.deleted_at) return prev.filter((t) => t.id !== row.id);
                return prev.map((t) => (t.id === row.id ? row : t));
              });
            } else if (ev === "DELETE") {
              setTeams((prev) => prev.filter((t) => t.id !== row.id));
            }
          }
        )
        .subscribe()
      : null;

    const subTeamChannel = supabase
      ? supabase
        .channel("public:sub_teams")
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "sub_teams" },
          (payload) => {
            if (!mounted) return;
            const ev = payload.eventType;
            const row = ev === "DELETE" ? payload.old : payload.new;
            if (!row) return;

            if (ev === "INSERT") {
              setSubTeams((prev) => {
                if (row.deleted_at) return prev.filter((st) => st.id !== row.id);
                const exists = prev.some((st) => st.id === row.id);
                if (exists) return prev.map((st) => (st.id === row.id ? row : st));
                return [...prev, row];
              });
            } else if (ev === "UPDATE") {
              setSubTeams((prev) => {
                if (row.deleted_at) return prev.filter((st) => st.id !== row.id);
                return prev.map((st) => (st.id === row.id ? row : st));
              });
            } else if (ev === "DELETE") {
              setSubTeams((prev) => prev.filter((st) => st.id !== row.id));
            }
          }
        )
        .subscribe()
      : null;

    const profileChannel = supabase
      ? supabase
        .channel("public:profiles")
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "profiles" },
          (payload) => {
            if (!mounted) return;
            const ev = payload.eventType;
            const row = ev === "DELETE" ? payload.old : payload.new;
            if (!row) return;

            if (ev === "INSERT") {
              setUsersList((prev) => {
                const exists = prev.some((u) => u.id === row.id);
                if (exists) return prev.map((u) => (u.id === row.id ? row : u));
                return [...prev, row];
              });
            } else if (ev === "UPDATE") {
              setUsersList((prev) => prev.map((u) => (u.id === row.id ? row : u)));
            } else if (ev === "DELETE") {
              setUsersList((prev) => prev.filter((u) => u.id !== row.id));
            }
          }
        )
        .subscribe()
      : null;

    const departmentChannel = supabase
      ? supabase
        .channel("public:departments")
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "departments" },
          (payload) => {
            if (!mounted) return;
            const ev = payload.eventType;
            const row = ev === "DELETE" ? payload.old : payload.new;
            if (!row) return;

            if (ev === "INSERT") {
              setDepartments((prev) => {
                const exists = prev.some((d) => d.id === row.id);
                if (exists) return prev.map((d) => (d.id === row.id ? row : d));
                return [...prev, row];
              });
            } else if (ev === "UPDATE") {
              setDepartments((prev) => prev.map((d) => (d.id === row.id ? row : d)));
            } else if (ev === "DELETE") {
              setDepartments((prev) => prev.filter((d) => d.id !== row.id));
            }
          }
        )
        .subscribe()
      : null;

    return () => {
      mounted = false;
      if (taskChannel && taskChannel.unsubscribe) taskChannel.unsubscribe();
      if (taskAssigneesChannel && taskAssigneesChannel.unsubscribe) {
        taskAssigneesChannel.unsubscribe();
      }
      if (teamChannel && teamChannel.unsubscribe) teamChannel.unsubscribe();
      if (subTeamChannel && subTeamChannel.unsubscribe) subTeamChannel.unsubscribe();
      if (profileChannel && profileChannel.unsubscribe) profileChannel.unsubscribe();
      if (departmentChannel && departmentChannel.unsubscribe) departmentChannel.unsubscribe();
    };
  }, [refreshUsers, currentUser, getUserTeamIds]);

  if (loading) return <div style={{ padding: 20 }}>Loading user…</div>;

  const isLoggedIn = !!currentUser;

  if (!isLoggedIn) {
    return (
      <Routes>
        <Route path="/auth/suite" element={<AuthSuitePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<AuthRedirect />} />
      </Routes>
    );
  }

  const normalizeDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) return d.toISOString().slice(0, 10);
    return String(d).slice(0, 10);
  };

  const handleCreateTask = async (taskData) => {
    const now = new Date().toISOString();

    const newTask = {
      title: (taskData.title || "Untitled").trim(),
      task_note: taskData.task_note || null,
      task_type: taskData.task_type || "regular",
      repeat_frequency: taskData.repeat_frequency || null,
      repeat_every: taskData.repeat_every || 1,
      repeat_start_date: normalizeDate(taskData.repeat_start_date),
      repeat_end_date: normalizeDate(taskData.repeat_end_date),
      repeat_occurrences: taskData.repeat_occurrences || null,
      repeat_days: taskData.repeat_days || [],

      loop_duration_value:
        taskData.loop_duration_value ?? taskData.loopDurationValue ?? 1,

      loop_duration_unit:
        taskData.loop_duration_unit ?? taskData.loopDurationUnit ?? "days",

      create_next_after_completion:
        taskData.create_next_after_completion ??
        taskData.createNextAfterCompletion ??
        false,

      description: (taskData.description || "").trim(),
      assignee: taskData.assignee || taskData.assigneeName || null,
      assignee_email: taskData.assignee_email || taskData.assigneeEmail || null,
      assignee_id: taskData.assignee_id || null,
      assigned_by: currentUser?.name || null,
      created_by: currentUser?.id || null,
      start_date: normalizeDate(taskData.start_date || taskData.startDate),
      due_date: normalizeDate(taskData.due_date || taskData.dueDate),
      priority: taskData.priority || "Medium",
      status: taskData.status || "Todo",
      budget: taskData.budget ? parseInt(taskData.budget, 10) : null,
      progress_status: taskData.progress_status || taskData.progressStatus || null,
      progress_report: taskData.progress_report || taskData.progressReport || null,
      extended_due_dates: taskData.extended_due_dates || [],
      hours: taskData.hours ? parseFloat(taskData.hours) : 0,
      created_at: now,
      updated_at: now,
      deleted_at: null,
      team_id: taskData.team_id || null,
      timer_start: taskData.timer_start || null,
      attachment_url: taskData.attachment_url || null,
      attachments: taskData.attachments || [],
      sub_team_id: taskData.sub_team_id || null,
      extended_due_date_1: taskData.extended_due_date_1 || null,
      extended_due_date_2: taskData.extended_due_date_2 || null
    };

    let savedTaskId = null;
    let savedTaskObj = null;

    try {
      const saved = await createTaskSupabase(newTask);
      if (saved?.id) {
        savedTaskId = saved.id;
        savedTaskObj = saved;
      } else {
        throw new Error("Task saved but no id returned.");
      }
    } catch (err) {
      console.error("createTaskSupabase failed, trying fallback insert:", err);
      try {
        const { data, error } = await supabase
          .from("tasks")
          .insert([newTask])
          .select("*")
          .single();

        if (error) throw error;
        if (data?.id) {
          savedTaskId = data.id;
          savedTaskObj = data;
        } else {
          throw new Error("Fallback task saved but no id returned.");
        }
      } catch (e) {
        console.error("fallback insert failed:", e);
        showAlert("Database error saving new task: " + (e.message || e));
        return null;
      }
    }

    if (!savedTaskId) return null;

    // Process assignees, user cases, issues, notifications, emails
    try {
      const cleanAssignedUsers = [
        ...new Map(
          (taskData.assigned_users || taskData.assignees || [])
            .map((u) => {
              if (u?.id) {
                const matched = usersList.find((user) => user.id === u.id);
                return {
                  id: u.id,
                  name: u.name || matched?.name || matched?.full_name || u.email,
                  email: u.email || matched?.email || "",
                };
              }
              const matchedUser = usersList.find(
                (user) =>
                  user.email?.toLowerCase() === u?.email?.toLowerCase() ||
                  user.name?.toLowerCase() === u?.name?.toLowerCase()
              );
              return matchedUser
                ? {
                    id: matchedUser.id,
                    name: matchedUser.name || matchedUser.full_name,
                    email: matchedUser.email,
                  }
                : null;
            })
            .filter((u) => u?.id)
            .map((u) => [u.id, u])
        ).values(),
      ];

      const cleanUserCases = (taskData.user_cases || []).filter(
        (uc) => uc.title || uc.description || uc.acceptanceCriteria
      );

      // 1. Insert into task_assignees
      if (cleanAssignedUsers.length > 0) {
        await supabase.from("task_assignees").insert(
          cleanAssignedUsers.map((user) => ({
            task_id: savedTaskId,
            user_id: user.id,
            assigned_by: currentUser?.id || null,
          }))
        );
      }

      // 2. Insert into task_user_cases
      if (cleanUserCases.length > 0) {
        await supabase.from("task_user_cases").insert(
          cleanUserCases.map((uc) => ({
            task_id: savedTaskId,
            title: uc.title || "Untitled User Story",
            description: uc.description || null,
            acceptance_criteria: uc.acceptanceCriteria || null,
            test_cases: (uc.testCases || []).map((t) => ({
              title: t,
              status: "Pending",
            })),
            test_cases_count: (uc.testCases || []).length,
            attachments: uc.files || [],
            status: "Pending",
            created_by: currentUser?.id || null,
          }))
        );
      }

      // 3. Insert into task_issues
      if (taskData.issue_title || taskData.issue_description) {
        await supabase.from("task_issues").insert({
          task_id: savedTaskId,
          title: taskData.issue_title || "Untitled Issue",
          description: taskData.issue_description || null,
          priority: taskData.issue_priority || "Medium",
          status: "Open",
          reported_by: currentUser?.id || null,
          assigned_to: cleanAssignedUsers[0]?.id || null,
          attachments: taskData.issue_files || [],
        });
      }

      // 4. Notifications & Emails
      if (cleanAssignedUsers.length > 0 && newTask.task_type !== "loop") {
        const notificationRows = cleanAssignedUsers.map((user) => ({
          user_id: user.id,
          task_id: savedTaskId,
          title: "Task assigned",
          message: taskData.task_note || `You have been assigned "${taskData.title}"`,
          type: "TASK_CREATED",
          action_role: "assignee",
          assignee_name: user.name || user.email,
          assigned_to: user.name || user.email,
          attachments: taskData.attachments || [],
          is_read: false,
          is_dismissed: false,
        }));

        await supabase.from("notifications").insert(notificationRows);

        const emailUsers = cleanAssignedUsers.filter((u) => u.email);
        for (const user of emailUsers) {
          try {
            const baseUrl = process.env.NODE_ENV === "development" ? "http://localhost:5000" : "";
            await fetch(`${baseUrl}/api/email/task-created`, {
              method: "POST",
              headers: { "Content-Type": "application/json", "x-internal-token": process.env.REACT_APP_INTERNAL_TOKEN || "" },
              body: JSON.stringify({
                users: [user],
                task: {
                  id: savedTaskId,
                  title: taskData.title,
                  description: taskData.task_note || taskData.description,
                  assigned_to: user.name || user.email,
                  assigned_by: currentUser?.name || currentUser?.email || "N/A",
                  start_date: taskData.start_date,
                  due_date: taskData.due_date,
                  priority: taskData.priority,
                  attachments: taskData.attachments || [],
                },
                taskUrl: `${window.location.origin}/tasks/${savedTaskId}`,
              }),
            });
          } catch (emailError) {
            console.error("Task email failed:", emailError);
          }
        }
      }

      if (newTask.task_type === "loop") {
        try {
          const baseUrl = process.env.NODE_ENV === "development" ? "http://localhost:5000" : "";
          const res = await fetch(`${baseUrl}/api/cron/generate-loop-occurrences`, {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "x-internal-token": process.env.REACT_APP_INTERNAL_TOKEN || "" 
            },
            body: JSON.stringify({ task_id: savedTaskId })
          });
          let result;
          const contentType = res.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            result = await res.json();
          } else {
            const textResponse = await res.text();
            throw new Error(`Server returned non-JSON response: ${textResponse.slice(0, 100)}`);
          }

          if (!res.ok) {
            throw new Error(result.error || "Failed to generate loop occurrences");
          }

          if (result.success && result.occurrencesGenerated >= 0) {
            showAlert("Loop task schedule created successfully. Today's occurrence generated.", "success");
            
            // Fetch generated occurrences
            const { data: occurrences } = await supabase
              .from("tasks")
              .select(TASK_SELECT)
              .eq("loop_series_id", savedTaskId);
            
            if (occurrences && occurrences.length > 0) {
              setTasks(prev => {
                const newTasks = [...prev];
                occurrences.forEach(occ => {
                  if (!newTasks.find(t => t.id === occ.id)) newTasks.push(occ);
                });
                return newTasks;
              });
            }
          } else if (result.success) {
            showAlert("Loop task schedule created successfully.", "success");
          } else {
            showAlert("Loop schedule saved, but today's occurrence was not generated: " + (result.error || "Unknown Error"), "error");
          }
        } catch (e) {
          console.error("Failed to trigger loop generation:", e);
          showAlert("Loop schedule saved, but today's occurrence was not generated: " + e.message, "error");
        }
      } else {
         showAlert("Task created successfully", "success");
      }

    } catch (childrenErr) {
      console.error("Failed to insert task children or notify:", childrenErr);
    }

    // Fetch full task with joins so state is completely populated
    try {
      const { data: fullTask, error: fetchError } = await supabase
        .from("tasks")
        .select(TASK_SELECT)
        .eq("id", savedTaskId)
        .single();

      if (!fetchError && fullTask) {
        setTasks((prev) => {
          const exists = prev.some((t) => t.id === fullTask.id);
          if (exists) {
            return prev.map((t) => (t.id === fullTask.id ? fullTask : t));
          }
          return [...prev, fullTask];
        });
        return fullTask;
      }
    } catch (fErr) {
      console.error("Failed to fetch full task:", fErr);
    }

    // Fallback: put raw saved row into tasks state
    if (savedTaskObj) {
      setTasks((prev) => {
        const exists = prev.some((t) => t.id === savedTaskObj.id);
        if (exists) {
          return prev.map((t) => (t.id === savedTaskObj.id ? savedTaskObj : t));
        }
        return [...prev, savedTaskObj];
      });
      return savedTaskObj;
    }

    return null;
  };

  const handleUpdateTask = async (taskId, updates = {}) => {
    const now = new Date().toISOString();
    const payload = { ...updates, updated_at: now };

    if (updates.startDate) payload.start_date = normalizeDate(updates.startDate);
    if (updates.dueDate) payload.due_date = normalizeDate(updates.dueDate);
    if (updates.repeat_start_date) payload.repeat_start_date = normalizeDate(updates.repeat_start_date);
    if (updates.repeat_end_date) payload.repeat_end_date = normalizeDate(updates.repeat_end_date);

    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...payload } : t)));

    try {
      const saved = await updateTaskSupabase(taskId, payload);
      if (saved) {
        setTasks((prev) => prev.map((t) => (t.id === taskId ? saved : t)));
        return saved;
      }
      return null;
    } catch (err) {
      console.error("updateTaskSupabase failed:", err);

      try {
        const { data, error } = await supabase
          .from("tasks")
          .update(payload)
          .eq("id", taskId)
          .select("*")
          .single();

        if (error) throw error;

        if (data) {
          setTasks((prev) => prev.map((t) => (t.id === taskId ? data : t)));
          return data;
        }
      } catch (e) {
        console.error("fallback update failed:", e);
        showAlert("Database error updating task: " + (e.message || e));
        throw e;
      }
    }
  };

  const handleDeleteTask = async (taskId, deleteSeries = false) => {
    const now = new Date().toISOString();
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const cleanDependencies = async (id) => {
      try {
        await supabase.from("task_assignees").delete().eq("task_id", id);
        await supabase.from("notifications").delete().eq("task_id", id);
        await supabase.from("task_notes").delete().eq("task_id", id);
        await supabase.from("task_issues").delete().eq("task_id", id);
        await supabase.from("task_user_cases").delete().eq("task_id", id);
        await supabase.from("task_extension_requests").delete().eq("task_id", id);
      } catch (err) {
        console.error("Error cleaning dependencies for task", id, err);
      }
    };

    if (deleteSeries) {
      const seriesId = task.is_loop_occurrence ? task.loop_series_id : task.id;

      setTasks((prev) =>
        prev.filter((t) => t.id !== seriesId && t.loop_series_id !== seriesId)
      );

      try {
        const occurrences = tasks.filter((t) => t.id === seriesId || t.loop_series_id === seriesId);
        for (const occ of occurrences) {
          await cleanDependencies(occ.id);
        }
        await supabase.from("tasks").delete().eq("loop_series_id", seriesId);
        await supabase.from("tasks").delete().eq("id", seriesId);
        return true;
      } catch (err) {
        console.error("Error hard deleting series:", err);
        // Fallback to soft delete
        await supabase.from("tasks").update({ deleted_at: now }).eq("id", seriesId);
        await supabase.from("tasks").update({ deleted_at: now }).eq("loop_series_id", seriesId);
        return true; // Still considered successful from a user perspective
      }
    } else {
      setTasks((prev) => prev.filter((t) => t.id !== taskId));

      try {
        await cleanDependencies(taskId);
        
        if (task.is_loop_occurrence || task.task_type === "loop") {
          // Soft delete loop occurrence so generator doesn't recreate it
          await supabase.from("tasks").update({ deleted_at: now }).eq("id", taskId);
        } else {
          // Hard delete regular task
          await supabase.from("tasks").delete().eq("id", taskId);
        }
        return true;
      } catch (err) {
        console.error("Task delete failed:", err);
        // Fallback soft-delete
        try {
          await supabase.from("tasks").update({ deleted_at: now }).eq("id", taskId);
          return true;
        } catch (e) {
          console.error("fallback soft-delete failed:", e);
          return false;
        }
      }
    }
  };

  const handleCreateTeam = async (teamData) => {
    const now = new Date().toISOString();
    const newTeam = {
      id: teamData.id || crypto.randomUUID(),
      name: teamData.name || "New Team",
      department_id: teamData.department_id || null,
      created_at: now
    };

    setTeams((prev) => [...prev, newTeam]);

    try {
      const { data, error } = await supabase.from("teams").insert([newTeam]).select().single();
      if (error) throw error;

      if (data) {
        setTeams((prev) => prev.map((t) => (t.id === newTeam.id ? data : t)));
        return data;
      }
    } catch (err) {
      console.error("persist team failed:", err);
      setTeams((prev) => prev.filter((t) => t.id !== newTeam.id));
      showAlert("Failed to create team: " + (err.message || "Unknown error"));
      return null;
    }

    return newTeam;
  };

  const handleUpdateTeam = async (teamId, updates) => {
    const payload = { ...updates };
    setTeams((prev) => prev.map((t) => (t.id === teamId ? { ...t, ...payload } : t)));

    try {
      const { data, error } = await supabase.from("teams").update(payload).eq("id", teamId).select().single();
      if (!error && data) {
        setTeams((prev) => prev.map((t) => (t.id === teamId ? data : t)));
        return data;
      }
    } catch (err) {
      console.error("update team failed:", err);
    }

    return null;
  };

  const handleDeleteTeam = async (teamId) => {
    const now = new Date().toISOString();
    setTeams((prev) => prev.filter((t) => t.id !== teamId));

    try {
      await supabase.from("teams").update({ deleted_at: now }).eq("id", teamId);
      return true;
    } catch (err) {
      console.error("soft-delete team failed:", err);
      return false;
    }
  };

  const handleCreateSubTeam = async (subTeamData) => {
    const now = new Date().toISOString();
    const newSubTeam = {
      id: subTeamData.id || crypto.randomUUID(),
      name: subTeamData.name || "New Sub Team",
      team_id: subTeamData.team_id,
      created_at: now
    };

    setSubTeams((prev) => [...prev, newSubTeam]);

    try {
      const { data, error } = await supabase.from("sub_teams").insert([newSubTeam]).select().single();
      if (error) throw error;

      if (data) {
        setSubTeams((prev) => prev.map((st) => (st.id === newSubTeam.id ? data : st)));
        return data;
      }
    } catch (err) {
      console.error("persist sub-team failed:", err);
      setSubTeams((prev) => prev.filter((st) => st.id !== newSubTeam.id));
      showAlert("Failed to create sub-team: " + (err.message || "Unknown error"));
      return null;
    }

    return newSubTeam;
  };

  const handleDeleteSubTeam = async (subTeamId) => {
    const now = new Date().toISOString();
    setSubTeams((prev) => prev.filter((st) => st.id !== subTeamId));

    try {
      await supabase.from("sub_teams").update({ deleted_at: now }).eq("id", subTeamId);
      return true;
    } catch (err) {
      console.error("soft-delete sub-team failed:", err);
      return false;
    }
  };

  const handleCreateDepartment = async (deptData) => {
    const newDept = {
      id: crypto.randomUUID(),
      name: deptData.name,
      created_at: new Date().toISOString()
    };

    setDepartments((prev) => [...prev, newDept]);

    try {
      const { data, error } = await supabase.from("departments").insert([newDept]).select().single();
      if (error) throw error;

      if (data) {
        setDepartments((prev) => prev.map((d) => (d.id === newDept.id ? data : d)));
        return data;
      }
    } catch (err) {
      console.error("Failed to create department:", err);
      setDepartments((prev) => prev.filter((d) => d.id !== newDept.id));
      showAlert("Failed to create department: " + (err.message || "Unknown error"));
    }

    return null;
  };

  const handleUpdateDepartment = async (id, updates) => {
    setDepartments((prev) => prev.map((d) => (d.id === id ? { ...d, ...updates } : d)));

    try {
      const { data, error } = await supabase.from("departments").update(updates).eq("id", id).select().single();
      if (error) throw error;

      if (data) {
        setDepartments((prev) => prev.map((d) => (d.id === id ? data : d)));
        return data;
      }
    } catch (err) {
      console.error("Failed to update department:", err);
    }

    return null;
  };

  const handleDeleteDepartment = async (id) => {
    const original = [...departments];
    setDepartments((prev) => prev.filter((d) => d.id !== id));

    try {
      const { error } = await supabase.from("departments").delete().eq("id", id);
      if (error) throw error;
      return true;
    } catch (err) {
      console.error("Failed to delete department:", err);
      setDepartments(original);
      showAlert("Failed to delete department. It may be in use by teams.");
      return false;
    }
  };

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${darkMode ? "dark bg-gray-900" : "bg-gray-100"}`}>
      {isLoggedIn && <TopBar darkMode={darkMode} setDarkMode={setDarkMode} />}

      <div className="flex flex-1 min-h-0 relative">
        {isLoggedIn && (
          <LeftSidebar
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            darkMode={darkMode}
          />
        )}

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <main className={`flex-1 p-6 overflow-y-auto min-h-0 ${darkMode ? "bg-gray-900" : ""}`}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              <Route
                path="/dashboard"
                element={
                  <Dashboard
                    tasks={tasks}
                    teams={teams}
                    subTeams={subTeams}
                    departments={departments}
                    userMemberships={userMemberships}
                    onCreateTask={handleCreateTask}
                    onUpdateTask={handleUpdateTask}
                    users={usersList}
                    onRefreshUsers={refreshUsers}
                    darkMode={darkMode}
                  />
                }
              />

              <Route
                path="/tasks"
                element={
                  <TaskList
                    tasks={tasks}
                    teams={teams}
                    subTeams={subTeams}
                    departments={departments}
                    users={usersList}
                    userMemberships={userMemberships}
                    onCreateTask={handleCreateTask}
                    onUpdateTask={handleUpdateTask}
                    onDeleteTask={handleDeleteTask}
                    onRefreshUsers={refreshUsers}
                    onRefreshTasks={async () => {
                      await getUserTeamIds();

                      const { data, error } = await supabase
                        .from("tasks")
                        .select(TASK_SELECT)
                        .is("deleted_at", null)
                        .order("created_at", { ascending: true });

                      if (error) {
                        console.error("Refresh tasks failed:", error);
                        return;
                      }

                      setTasks(data || []);
                    }}
                    darkMode={darkMode}
                  />
                }
              />

              <Route
                path="/tasks/new"
                element={
                  <TaskForm
                    users={usersList}
                    currentUser={currentUser}
                    teams={teams}
                    subTeams={subTeams}
                    darkMode={darkMode}
                    onSave={handleCreateTask}
                  />
                }
              />

              <Route
                path="/tasks/:taskId"
                element={
                  <TaskFormRoute
                    currentUser={currentUser}
                    users={usersList}
                    teams={teams}
                    subTeams={subTeams}
                    darkMode={darkMode}
                    onUpdateTaskInState={handleUpdateTaskInState}
                    onDeleteTask={handleDeleteTask}
                  />
                }
              />

              <Route
                path="/teams"
                element={
                  currentUser?.role === "Global Admin" ||
                    currentUser?.role === "Operational Admin" ||
                    currentUser?.role === "Department Admin" ||
                    currentUser?.can_manage_teams ? (
                    <Teams
                      teams={teams}
                      subTeams={subTeams}
                      departments={departments}
                      tasks={tasks}
                      onCreateTeam={handleCreateTeam}
                      onDeleteTeam={handleDeleteTeam}
                      onUpdateTeam={handleUpdateTeam}
                      onCreateSubTeam={handleCreateSubTeam}
                      onDeleteSubTeam={handleDeleteSubTeam}
                      users={usersList}
                      onCreateTask={handleCreateTask}
                      onRefreshUsers={refreshUsers}
                      onRefreshTeams={refreshTeams}
                      onCreateDepartment={handleCreateDepartment}
                      onUpdateDepartment={handleUpdateDepartment}
                      onDeleteDepartment={handleDeleteDepartment}
                      darkMode={darkMode}
                    />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />

              <Route
                path="/calendar"
                element={
                  <CalendarView
                    tasks={tasks}
                    teams={teams}
                    subTeams={subTeams}
                    users={usersList}
                    onUpdateTask={handleUpdateTask}
                    onDeleteTask={handleDeleteTask}
                    onCreateTask={handleCreateTask}
                    onRefreshUsers={refreshUsers}
                    darkMode={darkMode}
                  />
                }
              />

              <Route
                path="/users"
                element={
                  currentUser?.role !== "User" || currentUser?.can_manage_users ? (
                    <Users
                      subTeams={subTeams}
                      onRefreshUsers={refreshUsers}
                      onRefreshTeams={refreshTeams}
                      darkMode={darkMode}
                    />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />

              <Route path="/auth/suite" element={<AuthSuitePage />} />
              <Route path="/login" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
              <Route path="/change-log" element={<ChangeLog />} />
            </Routes>
            <ScrollToTop />
          </main>
        </div>
      </div>
    </div>
  );
}